import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { labInventoryAPI } from '../../utils/api';
import useDebounce from '../../utils/useDebounce';

const CATEGORIES = ['Reagent', 'Consumable', 'Chemical', 'Equipment', 'Glassware', 'PPE', 'Other'];

const CAT_COLORS = {
    Reagent: '#7c3aed', Consumable: '#0369a1', Chemical: '#dc2626',
    Equipment: '#059669', Glassware: '#d97706', PPE: '#0891b2', Other: '#64748b'
};

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 };

function stockStatus(item) {
    if (item.current_stock <= 0) return { label: 'Out of Stock', color: '#dc2626', bg: '#fef2f2' };
    if (parseFloat(item.current_stock) <= parseFloat(item.min_stock)) return { label: 'Low Stock', color: '#d97706', bg: '#fffbeb' };
    return { label: 'Available', color: '#16a34a', bg: '#f0fdf4' };
}

function Modal({ title, onClose, children }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 440, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}

export default function LabInventory() {
    const [tab, setTab] = useState('dashboard');
    const [dashboard, setDashboard] = useState(null);
    const [items, setItems] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const debouncedSearch = useDebounce(search, 400);

    // Modals
    const [addItemModal, setAddItemModal] = useState(false);
    const [addSupplierModal, setAddSupplierModal] = useState(false);
    const [stockModal, setStockModal] = useState(null); // { item, mode: 'add'|'use'|'adjust' }
    const [editModal, setEditModal] = useState(null);

    // Forms
    const [itemForm, setItemForm] = useState({ item_code: '', item_name: '', category: 'Reagent', unit: '', min_stock: '', max_stock: '', unit_price: '', supplier_id: '', description: '', batch_number: '', expiry_date: '' });
    const [supplierForm, setSupplierForm] = useState({ name: '', contact_person: '', phone: '', email: '', address: '' });
    const [stockForm, setStockForm] = useState({ quantity: '', reference_no: '', notes: '', new_quantity: '' });

    const setIF = (k, v) => setItemForm(f => ({ ...f, [k]: v }));
    const setSF = (k, v) => setSupplierForm(f => ({ ...f, [k]: v }));
    const setStF = (k, v) => setStockForm(f => ({ ...f, [k]: v }));

    useEffect(() => { loadDashboard(); }, []);
    useEffect(() => { if (tab === 'items') loadItems(); }, [tab, debouncedSearch, catFilter, statusFilter]);
    useEffect(() => { if (tab === 'transactions') loadTransactions(); }, [tab]);
    useEffect(() => { if (tab === 'suppliers' || addItemModal) loadSuppliers(); }, [tab, addItemModal]);

    const loadDashboard = async () => {
        try {
            const res = await labInventoryAPI.getDashboard();
            setDashboard(res.data);
        } catch { toast.error('Failed to load dashboard'); }
    };

    const loadItems = async () => {
        setLoading(true);
        try {
            const res = await labInventoryAPI.getItems({ q: debouncedSearch || undefined, category: catFilter || undefined, status: statusFilter || undefined });
            setItems(res.data.items);
        } catch { toast.error('Failed to load items'); }
        finally { setLoading(false); }
    };

    const loadSuppliers = async () => {
        try { const res = await labInventoryAPI.getSuppliers(); setSuppliers(res.data.suppliers); } catch {}
    };

    const loadTransactions = async () => {
        setLoading(true);
        try { const res = await labInventoryAPI.getTransactions({ limit: 100 }); setTransactions(res.data.transactions); }
        catch { toast.error('Failed to load transactions'); }
        finally { setLoading(false); }
    };

    const handleAddItem = async () => {
        if (!itemForm.item_code || !itemForm.item_name || !itemForm.unit) return toast.error('Code, name and unit required');

        setLoading(true);
        try {
            await labInventoryAPI.addItem(itemForm);
            toast.success('Item added');
            setAddItemModal(false);
            setItemForm({ item_code: '', item_name: '', category: 'Reagent', unit: '', min_stock: '', max_stock: '', unit_price: '', supplier_id: '', description: '', batch_number: '', expiry_date: '' });
            loadItems(); loadDashboard();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add item');
        } finally { setLoading(false); }
    };

    const handleAddSupplier = async () => {
        if (!supplierForm.name) return toast.error('Supplier name required');
        setLoading(true);
        try {
            await labInventoryAPI.addSupplier(supplierForm);
            toast.success('Supplier added');
            setAddSupplierModal(false);
            setSupplierForm({ name: '', contact_person: '', phone: '', email: '', address: '' });
            loadSuppliers();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add supplier');
        } finally { setLoading(false); }
    };

    const handleStockAction = async () => {
        const { item, mode } = stockModal;
        setLoading(true);
        try {
            if (mode === 'add') await labInventoryAPI.addStock(item.id, { quantity: stockForm.quantity, reference_no: stockForm.reference_no, notes: stockForm.notes });
            else if (mode === 'use') await labInventoryAPI.useStock(item.id, { quantity: stockForm.quantity, notes: stockForm.notes });
            else await labInventoryAPI.adjustStock(item.id, { new_quantity: stockForm.new_quantity, notes: stockForm.notes });
            toast.success(mode === 'add' ? 'Stock added' : mode === 'use' ? 'Stock used' : 'Stock adjusted');
            setStockModal(null);
            setStockForm({ quantity: '', reference_no: '', notes: '', new_quantity: '' });
            loadItems(); loadDashboard();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Action failed');
        } finally { setLoading(false); }
    };

    const handleUpdateItem = async () => {
        setLoading(true);
        try {
            await labInventoryAPI.updateItem(editModal.id, editModal);
            toast.success('Item updated');
            setEditModal(null);
            loadItems();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Update failed');
        } finally { setLoading(false); }
    };

    const tabs = [
        { key: 'dashboard', label: '📊 Dashboard' },
        { key: 'items', label: '📦 Stock Items' },
        { key: 'transactions', label: '📋 Transactions' },
        { key: 'suppliers', label: '🏢 Suppliers' },
    ];

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>🧪</span>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Lab Inventory</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Reagents, Consumables & Supplies</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setAddSupplierModal(true)}
                        style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        🏢 Add Supplier
                    </button>
                    <button onClick={() => { setAddItemModal(true); setTab('items'); }}
                        style={{ padding: '8px 16px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        + Add Item
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{ padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: tab === t.key ? '#7c3aed' : '#f1f5f9',
                            color: tab === t.key ? 'white' : '#374151' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* DASHBOARD TAB */}
            {tab === 'dashboard' && dashboard && (
                <div>
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                        {[
                            { label: 'Total Items', value: dashboard.summary.total_items, color: '#7c3aed', icon: '📦' },
                            { label: 'Low Stock', value: dashboard.summary.low_stock, color: '#d97706', icon: '⚠️' },
                            { label: 'Out of Stock', value: dashboard.summary.out_of_stock, color: '#dc2626', icon: '🚫' },
                            { label: 'Stock Value (Rs)', value: `${parseFloat(dashboard.summary.total_value).toLocaleString()}`, color: '#059669', icon: '💰' },
                            { label: 'Expiring (30d)', value: (parseInt(dashboard.summary.expiring_soon)||0) + (parseInt(dashboard.summary.expired)||0), color: '#dc2626', icon: '🔴' },
                        ].map(c => (
                            <div key={c.label} style={{ background: 'white', borderRadius: 12, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: `4px solid ${c.color}` }}>
                                <div style={{ fontSize: 22, marginBottom: 6 }}>{c.icon}</div>
                                <div style={{ fontSize: 24, fontWeight: 800, color: c.color }}>{c.value}</div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>{c.label}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {/* Low Stock Alerts */}
                        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 14 }}>⚠️ Low Stock Alerts</h3>
                            {dashboard.low_stock_items.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 20, color: '#64748b', fontSize: 13 }}>✅ All items adequately stocked</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {dashboard.low_stock_items.map(item => {
                                            const st = stockStatus(item);
                                            return (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px 0' }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.item_name}</div>
                                                        <div style={{ fontSize: 11, color: '#64748b' }}>{item.item_code}</div>
                                                    </td>
                                                    <td style={{ textAlign: 'right', padding: '8px 0' }}>
                                                        <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                                                            {item.current_stock} {item.unit}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Expiry Alerts */}
                        {dashboard.expiring_items && dashboard.expiring_items.length > 0 && (
                            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #fca5a5' }}>
                                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 14 }}>🔴 Expiry Alerts</h3>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {dashboard.expiring_items.map(item => {
                                            const exp = new Date(item.expiry_date);
                                            const today = new Date(); today.setHours(0,0,0,0);
                                            const diffDays = Math.floor((exp - today) / 86400000);
                                            const expStr = exp.toLocaleDateString('en-PK', { day:'2-digit', month:'short', year:'numeric' });
                                            return (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px 0' }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.item_name}</div>
                                                        <div style={{ fontSize: 11, color: '#64748b' }}>{item.item_code} — {item.category}</div>
                                                    </td>
                                                    <td style={{ textAlign: 'right', padding: '8px 0' }}>
                                                        <span style={{ background: diffDays < 0 ? '#fef2f2' : '#fffbeb', color: diffDays < 0 ? '#dc2626' : '#d97706', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                                                            {diffDays < 0 ? '🔴 EXPIRED' : `🟠 ${expStr}`}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Recent Transactions */}
                        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 14 }}>📋 Recent Transactions</h3>
                            {dashboard.recent_transactions.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 20, color: '#64748b', fontSize: 13 }}>No transactions yet</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {dashboard.recent_transactions.map(t => (
                                            <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '7px 0' }}>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{t.item_name}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b' }}>{new Date(t.created_at).toLocaleString('en-PK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                                                </td>
                                                <td style={{ textAlign: 'right', padding: '7px 0' }}>
                                                    <span style={{ fontSize: 11, fontWeight: 700,
                                                        color: t.transaction_type === 'Purchase' ? '#16a34a' : t.transaction_type === 'Usage' ? '#dc2626' : '#d97706' }}>
                                                        {t.transaction_type === 'Purchase' ? '+' : t.transaction_type === 'Usage' ? '-' : '~'}{Math.abs(t.quantity)} {t.unit}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ITEMS TAB */}
            {tab === 'items' && (
                <div>
                    {/* Filters */}
                    <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadItems()}
                            placeholder="Search item name or code..."
                            style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                            style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, background: 'white' }}>
                            <option value="">All Categories</option>
                            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                            style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, background: 'white' }}>
                            <option value="">All Status</option>
                            <option value="ok">Available</option>
                            <option value="low">Low Stock</option>
                            <option value="out">Out of Stock</option>
                        </select>
                        <button onClick={loadItems} style={{ padding: '9px 18px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            🔍 Search
                        </button>
                    </div>

                    <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                        ) : items.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                                <div style={{ fontSize: 40, marginBottom: 10 }}>📦</div>
                                <p>No items found. <span style={{ color: '#7c3aed', cursor: 'pointer', fontWeight: 600 }} onClick={() => setAddItemModal(true)}>Add first item?</span></p>
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        {['Code', 'Item Name', 'Category', 'Stock', 'Min', 'Unit Price', 'Expiry', 'Status', 'Actions'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => {
                                        const st = stockStatus(item);
                                        return (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #f8fafc' }}
                                                onMouseOver={e => e.currentTarget.style.background = '#faf5ff'}
                                                onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                                <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#7c3aed', fontFamily: 'monospace' }}>{item.item_code}</td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.item_name}</div>
                                                    {item.batch_number && <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>Batch: {item.batch_number}</div>}
                                                    {item.supplier_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.supplier_name}</div>}
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <span style={{ background: CAT_COLORS[item.category] + '20', color: CAT_COLORS[item.category], padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                                        {item.category}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 800, color: st.color }}>{parseFloat(item.current_stock)} {item.unit}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{parseFloat(item.min_stock)} {item.unit}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 12, color: '#374151' }}>Rs {parseFloat(item.unit_price).toLocaleString()}</td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    {item.expiry_date ? (() => {
                                                        const exp = new Date(item.expiry_date);
                                                        const today = new Date(); today.setHours(0,0,0,0);
                                                        const diffDays = Math.floor((exp - today) / 86400000);
                                                        const expStr = exp.toLocaleDateString('en-PK', { day:'2-digit', month:'short', year:'numeric' });
                                                        if (diffDays < 0) return <span style={{ background: '#fef2f2', color: '#dc2626', padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>🔴 EXPIRED</span>;
                                                        if (diffDays <= 30) return <span style={{ background: '#fffbeb', color: '#d97706', padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>🟠 {expStr}</span>;
                                                        return <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>✅ {expStr}</span>;
                                                    })() : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <div style={{ display: 'flex', gap: 5 }}>
                                                        <button onClick={() => { setStockModal({ item, mode: 'add' }); setStockForm({ quantity: '', reference_no: '', notes: '', new_quantity: '' }); }}
                                                            style={{ padding: '4px 8px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                            +Stock
                                                        </button>
                                                        <button onClick={() => { setStockModal({ item, mode: 'use' }); setStockForm({ quantity: '', reference_no: '', notes: '', new_quantity: '' }); }}
                                                            style={{ padding: '4px 8px', background: '#fff7ed', color: '#ea580c', border: '1px solid #fdba74', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                            Use
                                                        </button>
                                                        <button onClick={() => setEditModal({ ...item })}
                                                            style={{ padding: '4px 8px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                            Edit
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* TRANSACTIONS TAB */}
            {tab === 'transactions' && (
                <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                    ) : transactions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No transactions yet</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    {['Date & Time', 'Item', 'Type', 'Qty', 'Balance', 'Notes', 'By'].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(t => {
                                    const typeColor = { Purchase: '#16a34a', Usage: '#dc2626', Adjustment: '#d97706', Expired: '#64748b', Return: '#0369a1' }[t.transaction_type] || '#374151';
                                    return (
                                        <tr key={t.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                {new Date(t.created_at).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </td>
                                            <td style={{ padding: '9px 12px' }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{t.item_name}</div>
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.item_code}</div>
                                            </td>
                                            <td style={{ padding: '9px 12px' }}>
                                                <span style={{ background: typeColor + '15', color: typeColor, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                                                    {t.transaction_type}
                                                </span>
                                            </td>
                                            <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 700, color: typeColor }}>
                                                {t.transaction_type === 'Purchase' ? '+' : t.transaction_type === 'Usage' ? '-' : ''}{Math.abs(parseFloat(t.quantity))} {t.unit}
                                            </td>
                                            <td style={{ padding: '9px 12px', fontSize: 13, color: '#374151' }}>{parseFloat(t.balance_after)} {t.unit}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{t.notes || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#374151' }}>{t.performed_by_name || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* SUPPLIERS TAB */}
            {tab === 'suppliers' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                        <button onClick={() => setAddSupplierModal(true)}
                            style={{ padding: '9px 18px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            + Add Supplier
                        </button>
                    </div>
                    <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                        {suppliers.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No suppliers added yet</div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        {['Supplier Name', 'Contact Person', 'Phone', 'Email', 'Address'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {suppliers.map(s => (
                                        <tr key={s.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{s.name}</td>
                                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>{s.contact_person || '—'}</td>
                                            <td style={{ padding: '10px 12px', fontSize: 13 }}>{s.phone || '—'}</td>
                                            <td style={{ padding: '10px 12px', fontSize: 13 }}>{s.email || '—'}</td>
                                            <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{s.address || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* ADD ITEM MODAL */}
            {addItemModal && (
                <Modal title="➕ Add New Item" onClose={() => setAddItemModal(false)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div><label style={labelStyle}>Item Code *</label><input style={inputStyle} value={itemForm.item_code} onChange={e => setIF('item_code', e.target.value)} placeholder="e.g. REA-001" /></div>
                        <div><label style={labelStyle}>Item Name *</label><input style={inputStyle} value={itemForm.item_name} onChange={e => setIF('item_name', e.target.value)} placeholder="e.g. CBC Reagent" /></div>
                        <div>
                            <label style={labelStyle}>Category *</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={itemForm.category} onChange={e => setIF('category', e.target.value)}>
                                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div><label style={labelStyle}>Unit *</label><input style={inputStyle} value={itemForm.unit} onChange={e => setIF('unit', e.target.value)} placeholder="e.g. Bottle, Box, Pack" /></div>
                        <div><label style={labelStyle}>Min Stock</label><input type="number" style={inputStyle} value={itemForm.min_stock} onChange={e => setIF('min_stock', e.target.value)} placeholder="0" /></div>
                        <div><label style={labelStyle}>Max Stock</label><input type="number" style={inputStyle} value={itemForm.max_stock} onChange={e => setIF('max_stock', e.target.value)} placeholder="0" /></div>
                        <div><label style={labelStyle}>Unit Price (Rs)</label><input type="number" style={inputStyle} value={itemForm.unit_price} onChange={e => setIF('unit_price', e.target.value)} placeholder="0" /></div>
                        <div>
                            <label style={labelStyle}>Supplier</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={itemForm.supplier_id} onChange={e => setIF('supplier_id', e.target.value)}>
                                <option value="">— Select Supplier —</option>
                                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div><label style={labelStyle}>Batch / Badge No</label><input style={inputStyle} value={itemForm.batch_number} onChange={e => setIF('batch_number', e.target.value)} placeholder="e.g. BT-2026-001" /></div>
                        <div><label style={labelStyle}>Expiry Date</label><input type="date" style={inputStyle} value={itemForm.expiry_date} onChange={e => setIF('expiry_date', e.target.value)} /></div>
                        <div style={{ gridColumn: '1/-1' }}>
                            <label style={labelStyle}>Description</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={itemForm.description} onChange={e => setIF('description', e.target.value)} placeholder="Optional notes..." />
                        </div>
                    </div>
                    <button disabled={loading} onClick={handleAddItem}
                        style={{ marginTop: 16, width: '100%', padding: '11px', background: loading ? '#9ca3af' : '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        {loading ? 'Adding...' : '✅ Add Item'}
                    </button>
                </Modal>
            )}

            {/* ADD SUPPLIER MODAL */}
            {addSupplierModal && (
                <Modal title="🏢 Add Supplier" onClose={() => setAddSupplierModal(false)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div><label style={labelStyle}>Supplier Name *</label><input style={inputStyle} value={supplierForm.name} onChange={e => setSF('name', e.target.value)} placeholder="Supplier company name" /></div>
                        <div><label style={labelStyle}>Contact Person</label><input style={inputStyle} value={supplierForm.contact_person} onChange={e => setSF('contact_person', e.target.value)} placeholder="Sales rep name" /></div>
                        <div><label style={labelStyle}>Phone</label><input style={inputStyle} value={supplierForm.phone} onChange={e => setSF('phone', e.target.value)} placeholder="0300-1234567" /></div>
                        <div><label style={labelStyle}>Email</label><input style={inputStyle} value={supplierForm.email} onChange={e => setSF('email', e.target.value)} placeholder="supplier@email.com" /></div>
                        <div><label style={labelStyle}>Address</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={supplierForm.address} onChange={e => setSF('address', e.target.value)} placeholder="Supplier address" /></div>
                    </div>
                    <button disabled={loading} onClick={handleAddSupplier}
                        style={{ marginTop: 16, width: '100%', padding: '11px', background: loading ? '#9ca3af' : '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        {loading ? 'Adding...' : '✅ Add Supplier'}
                    </button>
                </Modal>
            )}

            {/* STOCK ACTION MODAL */}
            {stockModal && (
                <Modal
                    title={stockModal.mode === 'add' ? `➕ Add Stock — ${stockModal.item.item_name}` : stockModal.mode === 'use' ? `📤 Use Stock — ${stockModal.item.item_name}` : `🔧 Adjust Stock — ${stockModal.item.item_name}`}
                    onClose={() => setStockModal(null)}>
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                        Current Stock: <strong style={{ color: stockStatus(stockModal.item).color }}>{parseFloat(stockModal.item.current_stock)} {stockModal.item.unit}</strong>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {stockModal.mode === 'adjust' ? (
                            <div><label style={labelStyle}>New Stock Quantity *</label><input type="number" style={inputStyle} value={stockForm.new_quantity} onChange={e => setStF('new_quantity', e.target.value)} placeholder="Enter exact quantity" min="0" /></div>
                        ) : (
                            <>
                                <div><label style={labelStyle}>Quantity *</label><input type="number" style={inputStyle} value={stockForm.quantity} onChange={e => setStF('quantity', e.target.value)} placeholder={`How many ${stockModal.item.unit}?`} min="0.01" step="0.01" /></div>
                                {stockModal.mode === 'add' && <div><label style={labelStyle}>Reference / Invoice No</label><input style={inputStyle} value={stockForm.reference_no} onChange={e => setStF('reference_no', e.target.value)} placeholder="e.g. INV-2026-001" /></div>}
                            </>
                        )}
                        <div><label style={labelStyle}>Notes</label><input style={inputStyle} value={stockForm.notes} onChange={e => setStF('notes', e.target.value)} placeholder="Optional notes" /></div>
                    </div>
                    <button disabled={loading} onClick={handleStockAction}
                        style={{ marginTop: 16, width: '100%', padding: '11px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: 'white',
                            background: loading ? '#9ca3af' : stockModal.mode === 'add' ? '#16a34a' : stockModal.mode === 'use' ? '#dc2626' : '#d97706' }}>
                        {loading ? 'Processing...' : stockModal.mode === 'add' ? '✅ Add Stock' : stockModal.mode === 'use' ? '📤 Use Stock' : '🔧 Adjust'}
                    </button>
                </Modal>
            )}

            {/* EDIT ITEM MODAL */}
            {editModal && (
                <Modal title={`✏️ Edit — ${editModal.item_name}`} onClose={() => setEditModal(null)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div><label style={labelStyle}>Item Name</label><input style={inputStyle} value={editModal.item_name} onChange={e => setEditModal(m => ({ ...m, item_name: e.target.value }))} /></div>
                        <div>
                            <label style={labelStyle}>Category</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={editModal.category} onChange={e => setEditModal(m => ({ ...m, category: e.target.value }))}>
                                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div><label style={labelStyle}>Unit</label><input style={inputStyle} value={editModal.unit} onChange={e => setEditModal(m => ({ ...m, unit: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Unit Price (Rs)</label><input type="number" style={inputStyle} value={editModal.unit_price} onChange={e => setEditModal(m => ({ ...m, unit_price: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Min Stock</label><input type="number" style={inputStyle} value={editModal.min_stock} onChange={e => setEditModal(m => ({ ...m, min_stock: e.target.value }))} /></div>
                        <div><label style={labelStyle}>Max Stock</label><input type="number" style={inputStyle} value={editModal.max_stock} onChange={e => setEditModal(m => ({ ...m, max_stock: e.target.value }))} /></div>
                        <div>
                            <label style={labelStyle}>Supplier</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={editModal.supplier_id || ''} onChange={e => setEditModal(m => ({ ...m, supplier_id: e.target.value || null }))}>
                                <option value="">— No Supplier —</option>
                                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div style={{ gridColumn: '1/-1' }}>
                            <label style={labelStyle}>Description</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={editModal.description || ''} onChange={e => setEditModal(m => ({ ...m, description: e.target.value }))} />
                        </div>
                    </div>
                    <button disabled={loading} onClick={handleUpdateItem}
                        style={{ marginTop: 16, width: '100%', padding: '11px', background: loading ? '#9ca3af' : '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        {loading ? 'Saving...' : '✅ Save Changes'}
                    </button>
                </Modal>
            )}
        </div>
    );
}
